/* eslint-disable i18next/no-literal-string */
import { useMutation } from "@tanstack/react-query";
import {
  Activity,
  Baby,
  CalendarHeart,
  Check,
  Clock3,
  HeartPulse,
  Siren,
  Stethoscope,
  Sun,
  UserPlus,
} from "lucide-react";
import { navigate } from "raviger";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import Page from "@/components/Common/Page";
import PatientIdentifierFilter from "@/components/Patient/PatientIdentifierFilter";
import {
  buildSpecializedEncounter,
  joinLocalDateTime,
} from "@/components/Patient/specializedRegistration";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import FacilityOrganizationSelector from "@/pages/Facility/settings/organizations/components/FacilityOrganizationSelector";
import {
  EncounterRead,
  EncounterRegistrationDetails,
  EncounterRegistrationType,
} from "@/types/emr/encounter/encounter";
import encounterApi from "@/types/emr/encounter/encounterApi";
import mutate from "@/Utils/request/mutate";

type RegistrationConfig = {
  title: string;
  description: string;
  icon: typeof Siren;
  accent: string;
  action: string;
};

const REGISTRATIONS: Record<EncounterRegistrationType, RegistrationConfig> = {
  emergency: {
    title: "Emergency Registration",
    description: "Triage the patient and start emergency care immediately.",
    icon: Siren,
    accent: "text-red-600 bg-red-50",
    action: "Register & Start Emergency Care",
  },
  day_care: {
    title: "Day Care Registration",
    description: "Plan a same-day procedure or treatment visit.",
    icon: Sun,
    accent: "text-teal-700 bg-teal-50",
    action: "Register Day Care Visit",
  },
  newborn: {
    title: "Newborn Registration",
    description: "Link a newborn with the mother and birth encounter.",
    icon: Baby,
    accent: "text-cyan-700 bg-cyan-50",
    action: "Register Newborn",
  },
  health_checkup: {
    title: "Health Check-Up Registration",
    description: "Register a planned preventive health check-up.",
    icon: HeartPulse,
    accent: "text-green-700 bg-green-50",
    action: "Register Health Check-Up",
  },
};

const REQUIRED_FIELDS: Record<EncounterRegistrationType, string[]> = {
  emergency: [
    "triage_level",
    "arrival_mode",
    "incident_datetime",
    "chief_complaint",
  ],
  day_care: [
    "service_or_procedure",
    "scheduled_start",
    "expected_discharge",
    "payer_type",
  ],
  newborn: [
    "mother_patient",
    "birth_datetime",
    "birth_order",
    "birth_weight_kg",
    "delivery_type",
    "place_of_birth",
  ],
  health_checkup: ["package", "visit_datetime", "payer_type"],
};

export function RegistrationHub({ facilityId }: { facilityId: string }) {
  return (
    <Page title="Patient Registration Methods">
      <div className="mx-auto max-w-5xl space-y-6 py-6">
        <div>
          <h1 className="text-3xl font-bold">Patient Registration Methods</h1>
          <p className="mt-1 text-gray-600">
            Choose the workflow that matches the patient&apos;s visit.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <RegistrationMethodCard
            title="Normal Patient Registration"
            description="Create a standard patient demographic record."
            icon={UserPlus}
            accent="bg-blue-50 text-blue-700"
            onClick={() => navigate(`/facility/${facilityId}/patient/create`)}
          />
          {(
            Object.entries(REGISTRATIONS) as [
              EncounterRegistrationType,
              RegistrationConfig,
            ][]
          ).map(([type, config]) => (
            <RegistrationMethodCard
              key={type}
              {...config}
              onClick={() =>
                navigate(`/facility/${facilityId}/patient/register/${type}`)
              }
            />
          ))}
        </div>
      </div>
    </Page>
  );
}

function RegistrationMethodCard({
  title,
  description,
  icon: Icon,
  accent,
  onClick,
}: Omit<RegistrationConfig, "action"> & { onClick: () => void }) {
  return (
    <Card
      className="group cursor-pointer p-6 transition hover:-translate-y-0.5 hover:shadow-md"
      onClick={onClick}
    >
      <div className="flex items-start gap-4">
        <div className={cn("rounded-xl p-3", accent)}>
          <Icon className="size-7" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-gray-950">{title}</h2>
          <p className="mt-1 text-sm text-gray-600">{description}</p>
          <div className="mt-4 text-sm font-medium text-primary-700">
            Start registration →
          </div>
        </div>
      </div>
    </Card>
  );
}

export function SpecializedRegistration({
  facilityId,
  registrationType,
}: {
  facilityId: string;
  registrationType: string;
}) {
  const type = registrationType as EncounterRegistrationType;
  const config = REGISTRATIONS[type];
  const [patientId, setPatientId] = useState<string>();
  const [patientName, setPatientName] = useState<string>();
  const [organizations, setOrganizations] = useState<string[]>([]);
  const [details, setDetails] = useState<EncounterRegistrationDetails>({});

  const { mutate: createEncounter, isPending } = useMutation({
    mutationFn: mutate(encounterApi.create),
    onSuccess: (encounter: EncounterRead) => {
      toast.success(`${config.title} completed`);
      navigate(
        `/facility/${facilityId}/patient/${encounter.patient.id}/encounter/${encounter.id}/updates`,
      );
    },
  });

  const missingConfiguration = !config;
  const canSubmit = Boolean(patientId && organizations.length);
  const Icon = config?.icon ?? Stethoscope;

  const normalizedDetails = useMemo(
    () => normalizeDetails(type, details),
    [details, type],
  );

  if (missingConfiguration) {
    return (
      <Page title="Registration method not found">
        <div className="mx-auto max-w-2xl py-10">
          <Card className="p-6">
            <h1 className="text-xl font-semibold">
              Registration method not found
            </h1>
            <Button
              className="mt-4"
              onClick={() =>
                navigate(`/facility/${facilityId}/patient/register`)
              }
            >
              View registration methods
            </Button>
          </Card>
        </div>
      </Page>
    );
  }

  const submit = () => {
    if (!patientId) {
      toast.error("Select a patient before registering the visit");
      return;
    }
    if (!organizations.length) {
      toast.error("Select the responsible department");
      return;
    }

    const missing = REQUIRED_FIELDS[type].filter((field) => {
      const value = normalizedDetails[field];
      return value === "" || value == null;
    });
    if (missing.length) {
      toast.error("Complete all required registration fields");
      return;
    }

    if (type === "newborn" && normalizedDetails.mother_patient === patientId) {
      toast.error("Mother and newborn must be different patient records");
      return;
    }

    const encounter = buildSpecializedEncounter({
      patientId,
      facilityId,
      organizationId: organizations[0],
      registrationType: type,
      details: normalizedDetails,
    });
    createEncounter(encounter);
  };

  return (
    <Page title={config.title} hideTitleOnPage>
      <div className="mx-auto max-w-6xl space-y-5 py-6">
        <div className="flex items-center gap-4">
          <div className={cn("rounded-2xl p-3", config.accent)}>
            <Icon className="size-8" />
          </div>
          <div>
            <div className="text-sm text-gray-500">Patients / Registration</div>
            <h1 className="text-3xl font-bold text-gray-950">{config.title}</h1>
            <p className="text-gray-600">{config.description}</p>
          </div>
        </div>

        <Card className="p-5">
          <div className="grid items-end gap-4 md:grid-cols-[1fr_auto]">
            <div className="space-y-2">
              <Label>Search existing patient *</Label>
              <PatientIdentifierFilter
                includeDemographicSearch
                patientId={patientId}
                patientName={patientName}
                hideScanButton
                placeholder="Search by name, mobile number, UHID, Aadhaar or ABHA"
                onSelect={(id, name) => {
                  setPatientId(id);
                  setPatientName(name);
                }}
              />
            </div>
            <Button
              variant="outline"
              onClick={() => navigate(`/facility/${facilityId}/patient/create`)}
            >
              <UserPlus className="size-4" />
              Register new patient
            </Button>
          </div>
        </Card>

        <Card className="p-5">
          <h2 className="mb-4 text-lg font-semibold">Visit ownership</h2>
          <FacilityOrganizationSelector
            facilityId={facilityId}
            selectableOrgTypes={["dept"]}
            value={organizations}
            onChange={(value) => setOrganizations(value ?? [])}
          />
        </Card>

        {type === "emergency" && (
          <EmergencyFields details={details} setDetails={setDetails} />
        )}
        {type === "day_care" && (
          <DayCareFields details={details} setDetails={setDetails} />
        )}
        {type === "newborn" && (
          <NewbornFields details={details} setDetails={setDetails} />
        )}
        {type === "health_checkup" && (
          <HealthCheckupFields details={details} setDetails={setDetails} />
        )}

        <div className="sticky bottom-0 flex justify-between border-t bg-white/95 py-4 backdrop-blur">
          <Button
            variant="outline"
            onClick={() => navigate(`/facility/${facilityId}/patient/register`)}
          >
            Cancel
          </Button>
          <Button
            variant="primary_gradient"
            disabled={!canSubmit || isPending}
            onClick={submit}
          >
            <Check className="size-4" />
            {isPending ? "Registering…" : config.action}
          </Button>
        </div>
      </div>
    </Page>
  );
}

function EmergencyFields(props: RegistrationFieldsProps) {
  return (
    <div className="space-y-5">
      <Section title="Triage & emergency details" icon={Siren}>
        <SelectField
          label="Triage Level"
          required
          value={stringValue(props.details.triage_level)}
          options={[
            ["critical", "Critical"],
            ["urgent", "Urgent"],
            ["stable", "Stable"],
          ]}
          onChange={(value) => setDetail(props, "triage_level", value)}
        />
        <SelectField
          label="Arrival Mode"
          required
          value={stringValue(props.details.arrival_mode)}
          options={[
            ["walk_in", "Walk-in"],
            ["ambulance", "Ambulance"],
            ["referral", "Referral"],
            ["transfer", "Transfer"],
          ]}
          onChange={(value) => setDetail(props, "arrival_mode", value)}
        />
        <TextField
          label="Brought By"
          value={stringValue(props.details.brought_by)}
          onChange={(value) => setDetail(props, "brought_by", value)}
        />
        <DateTimeFields prefix="incident" label="Incident Time" {...props} />
        <TextAreaField
          label="Chief Complaint"
          required
          value={stringValue(props.details.chief_complaint)}
          onChange={(value) => setDetail(props, "chief_complaint", value)}
        />
        <TextAreaField
          label="Initial Triage Note"
          value={stringValue(props.details.initial_triage_note)}
          onChange={(value) => setDetail(props, "initial_triage_note", value)}
        />
      </Section>

      <Section title="Initial vitals (if available)" icon={Activity}>
        {[
          ["pulse", "Pulse (bpm)"],
          ["systolic_bp", "Systolic BP"],
          ["diastolic_bp", "Diastolic BP"],
          ["spo2", "SpO₂ (%)"],
          ["temperature", "Temperature (°C)"],
        ].map(([key, label]) => (
          <TextField
            key={key}
            label={label}
            type="number"
            value={stringValue(props.details[key])}
            onChange={(value) => setDetail(props, key, value)}
          />
        ))}
        <CheckField
          label="Notify emergency team"
          checked={Boolean(props.details.notify_emergency_team)}
          onChange={(value) => setDetail(props, "notify_emergency_team", value)}
        />
        <CheckField
          label="Defer payment collection"
          checked={Boolean(props.details.defer_payment)}
          onChange={(value) => setDetail(props, "defer_payment", value)}
        />
      </Section>
    </div>
  );
}

function DayCareFields(props: RegistrationFieldsProps) {
  return (
    <div className="space-y-5">
      <Section title="Visit details" icon={CalendarHeart}>
        <TextField
          label="Service / Procedure"
          required
          value={stringValue(props.details.service_or_procedure)}
          onChange={(value) => setDetail(props, "service_or_procedure", value)}
        />
        <TextField
          label="Responsible Practitioner"
          value={stringValue(props.details.responsible_practitioner)}
          onChange={(value) =>
            setDetail(props, "responsible_practitioner", value)
          }
        />
        <DateTimeFields
          prefix="scheduled_start"
          label="Scheduled Start"
          {...props}
        />
        <DateTimeFields
          prefix="expected_discharge"
          label="Expected Discharge"
          {...props}
        />
        <TextAreaField
          label="Reason for Visit"
          value={stringValue(props.details.reason_for_visit)}
          onChange={(value) => setDetail(props, "reason_for_visit", value)}
        />
      </Section>
      <Section title="Clinical preparation" icon={Stethoscope}>
        <TextAreaField
          label="Pre-procedure Instructions"
          value={stringValue(props.details.pre_procedure_instructions)}
          onChange={(value) =>
            setDetail(props, "pre_procedure_instructions", value)
          }
        />
        {[
          ["allergies_reviewed", "Allergies reviewed"],
          ["consent_received", "Consent received"],
          ["fasting_required", "Fasting required"],
        ].map(([key, label]) => (
          <CheckField
            key={key}
            label={label}
            checked={Boolean(props.details[key])}
            onChange={(value) => setDetail(props, key, value)}
          />
        ))}
        <TextField
          label="Companion Details"
          value={stringValue(props.details.companion_details)}
          onChange={(value) => setDetail(props, "companion_details", value)}
        />
      </Section>
      <Section title="Billing & bed" icon={Clock3}>
        <TextField
          label="Day Care Location"
          value={stringValue(props.details.day_care_location)}
          onChange={(value) => setDetail(props, "day_care_location", value)}
        />
        <TextField
          label="Recliner / Bed"
          value={stringValue(props.details.recliner_or_bed)}
          onChange={(value) => setDetail(props, "recliner_or_bed", value)}
        />
        <TextField
          label="Package"
          value={stringValue(props.details.package)}
          onChange={(value) => setDetail(props, "package", value)}
        />
        <PayerField {...props} />
      </Section>
    </div>
  );
}

function NewbornFields(props: RegistrationFieldsProps) {
  return (
    <div className="space-y-5">
      <Section title="Link mother / birth encounter" icon={Baby}>
        <div className="space-y-2 md:col-span-2">
          <Label>Mother *</Label>
          <PatientIdentifierFilter
            includeDemographicSearch
            hideScanButton
            patientId={stringValue(props.details.mother_patient)}
            patientName={stringValue(props.details.mother_name)}
            placeholder="Search mother by name, UHID, mobile, Aadhaar or ABHA"
            onSelect={(id, name) => {
              setDetail(props, "mother_patient", id ?? "");
              setDetail(props, "mother_name", name ?? "");
            }}
          />
        </div>
        <TextField
          label="Birth Encounter ID"
          value={stringValue(props.details.birth_encounter)}
          onChange={(value) => setDetail(props, "birth_encounter", value)}
        />
      </Section>
      <Section title="Newborn details" icon={Baby}>
        <TextField
          label="Baby Name (optional)"
          value={stringValue(props.details.baby_name)}
          onChange={(value) => setDetail(props, "baby_name", value)}
        />
        <DateTimeFields
          prefix="birth"
          label="Date & Time of Birth"
          {...props}
        />
        <SelectField
          label="Sex"
          value={stringValue(props.details.sex)}
          options={[
            ["male", "Male"],
            ["female", "Female"],
            ["others", "Others"],
          ]}
          onChange={(value) => setDetail(props, "sex", value)}
        />
        <TextField
          label="Birth Order"
          required
          type="number"
          value={stringValue(props.details.birth_order)}
          onChange={(value) => setDetail(props, "birth_order", value)}
        />
        <TextField
          label="Birth Weight (kg)"
          required
          type="number"
          value={stringValue(props.details.birth_weight_kg)}
          onChange={(value) => setDetail(props, "birth_weight_kg", value)}
        />
        <TextField
          label="Length (cm)"
          type="number"
          value={stringValue(props.details.length_cm)}
          onChange={(value) => setDetail(props, "length_cm", value)}
        />
        <TextField
          label="Head Circumference (cm)"
          type="number"
          value={stringValue(props.details.head_circumference_cm)}
          onChange={(value) => setDetail(props, "head_circumference_cm", value)}
        />
      </Section>
      <Section title="Delivery details" icon={Stethoscope}>
        <SelectField
          label="Delivery Type"
          required
          value={stringValue(props.details.delivery_type)}
          options={[
            ["vaginal", "Vaginal"],
            ["caesarean", "Caesarean"],
            ["assisted", "Assisted"],
          ]}
          onChange={(value) => setDetail(props, "delivery_type", value)}
        />
        <TextField
          label="Gestational Age (weeks)"
          type="number"
          value={stringValue(props.details.gestational_age_weeks)}
          onChange={(value) => setDetail(props, "gestational_age_weeks", value)}
        />
        <SelectField
          label="Place of Birth"
          required
          value={stringValue(props.details.place_of_birth)}
          options={[
            ["facility", "This facility"],
            ["other_facility", "Other facility"],
            ["home", "Home"],
            ["other", "Other"],
          ]}
          onChange={(value) => setDetail(props, "place_of_birth", value)}
        />
        <TextField
          label="Attending Practitioner"
          value={stringValue(props.details.attending_practitioner)}
          onChange={(value) =>
            setDetail(props, "attending_practitioner", value)
          }
        />
        <TextField
          label="APGAR Score (1 min)"
          type="number"
          value={stringValue(props.details.apgar_1_min)}
          onChange={(value) => setDetail(props, "apgar_1_min", value)}
        />
        <TextField
          label="APGAR Score (5 min)"
          type="number"
          value={stringValue(props.details.apgar_5_min)}
          onChange={(value) => setDetail(props, "apgar_5_min", value)}
        />
      </Section>
      <Section title="Identity & care" icon={HeartPulse}>
        <SelectField
          label="Blood Group"
          value={stringValue(props.details.blood_group)}
          options={[
            ["A_positive", "A+"],
            ["A_negative", "A−"],
            ["B_positive", "B+"],
            ["B_negative", "B−"],
            ["AB_positive", "AB+"],
            ["AB_negative", "AB−"],
            ["O_positive", "O+"],
            ["O_negative", "O−"],
          ]}
          onChange={(value) => setDetail(props, "blood_group", value)}
        />
        <SelectField
          label="Rh Factor"
          value={stringValue(props.details.rh_factor)}
          options={[
            ["positive", "Positive"],
            ["negative", "Negative"],
          ]}
          onChange={(value) => setDetail(props, "rh_factor", value)}
        />
        <TextField
          label="Birth Certificate Number"
          value={stringValue(props.details.birth_certificate_number)}
          onChange={(value) =>
            setDetail(props, "birth_certificate_number", value)
          }
        />
        <CheckField
          label="Part of multiple birth"
          checked={Boolean(props.details.multiple_birth)}
          onChange={(value) => setDetail(props, "multiple_birth", value)}
        />
        <CheckField
          label="Admitted to NICU at birth"
          checked={Boolean(props.details.nicu_admission)}
          onChange={(value) => setDetail(props, "nicu_admission", value)}
        />
      </Section>
    </div>
  );
}

function HealthCheckupFields(props: RegistrationFieldsProps) {
  return (
    <div className="space-y-5">
      <Section title="Check-up details" icon={HeartPulse}>
        <TextField
          label="Check-Up Package"
          required
          value={stringValue(props.details.package)}
          onChange={(value) => setDetail(props, "package", value)}
        />
        <DateTimeFields
          prefix="visit"
          label="Visit Date & Preferred Time"
          {...props}
        />
        <TextField
          label="Referring Organization"
          value={stringValue(props.details.referring_organization)}
          onChange={(value) =>
            setDetail(props, "referring_organization", value)
          }
        />
        <TextAreaField
          label="Purpose / Notes"
          value={stringValue(props.details.purpose_or_notes)}
          onChange={(value) => setDetail(props, "purpose_or_notes", value)}
        />
      </Section>
      <Section title="Visit setup" icon={CalendarHeart}>
        <TextField
          label="Check-Up Coordinator"
          value={stringValue(props.details.coordinator)}
          onChange={(value) => setDetail(props, "coordinator", value)}
        />
        <PayerField {...props} />
        {props.details.payer_type === "corporate" && (
          <TextField
            label="Corporate ID"
            value={stringValue(props.details.corporate_id)}
            onChange={(value) => setDetail(props, "corporate_id", value)}
          />
        )}
        <CheckField
          label="Fasting instructions provided"
          checked={Boolean(props.details.fasting_instructions_provided)}
          onChange={(value) =>
            setDetail(props, "fasting_instructions_provided", value)
          }
        />
        <CheckField
          label="Patient received preparation instructions"
          checked={Boolean(props.details.preparation_confirmed)}
          onChange={(value) => setDetail(props, "preparation_confirmed", value)}
        />
      </Section>
    </div>
  );
}

type RegistrationFieldsProps = {
  details: EncounterRegistrationDetails;
  setDetails: React.Dispatch<
    React.SetStateAction<EncounterRegistrationDetails>
  >;
};

function Section({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: typeof Activity;
  children: React.ReactNode;
}) {
  return (
    <Card className="p-5">
      <h2 className="mb-5 flex items-center gap-2 text-lg font-semibold">
        <Icon className="size-5 text-primary-700" />
        {title}
      </h2>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{children}</div>
    </Card>
  );
}

function TextField({
  label,
  value,
  onChange,
  required,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  type?: string;
}) {
  return (
    <div className="space-y-2">
      <Label>
        {label} {required && <span className="text-red-600">*</span>}
      </Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function TextAreaField({
  label,
  value,
  onChange,
  required,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
}) {
  return (
    <div className="space-y-2 md:col-span-2">
      <Label>
        {label} {required && <span className="text-red-600">*</span>}
      </Label>
      <Textarea value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
  required,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: [string, string][];
  required?: boolean;
}) {
  return (
    <div className="space-y-2">
      <Label>
        {label} {required && <span className="text-red-600">*</span>}
      </Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder={`Select ${label.toLowerCase()}`} />
        </SelectTrigger>
        <SelectContent>
          {options.map(([optionValue, optionLabel]) => (
            <SelectItem key={optionValue} value={optionValue}>
              {optionLabel}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function CheckField({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-3 rounded-lg border p-3 text-sm">
      <Checkbox
        checked={checked}
        onCheckedChange={(value) => onChange(value === true)}
      />
      {label}
    </label>
  );
}

function DateTimeFields({
  prefix,
  label,
  ...props
}: RegistrationFieldsProps & { prefix: string; label: string }) {
  return (
    <div className="space-y-2">
      <Label>{label} *</Label>
      <div className="grid grid-cols-2 gap-2">
        <Input
          type="date"
          value={stringValue(props.details[`${prefix}_date`])}
          onChange={(e) => setDetail(props, `${prefix}_date`, e.target.value)}
        />
        <Input
          type="time"
          value={stringValue(props.details[`${prefix}_time`])}
          onChange={(e) => setDetail(props, `${prefix}_time`, e.target.value)}
        />
      </div>
    </div>
  );
}

function PayerField(props: RegistrationFieldsProps) {
  return (
    <SelectField
      label="Payer Type"
      required
      value={stringValue(props.details.payer_type)}
      options={[
        ["self_pay", "Self Pay"],
        ["corporate", "Corporate"],
        ["insurance", "Insurance"],
        ["government_scheme", "Government Scheme"],
      ]}
      onChange={(value) => setDetail(props, "payer_type", value)}
    />
  );
}

function setDetail(
  props: RegistrationFieldsProps,
  key: string,
  value: string | boolean | undefined,
) {
  props.setDetails((current) => ({ ...current, [key]: value }));
}

function stringValue(value: unknown) {
  return typeof value === "string" || typeof value === "number"
    ? String(value)
    : "";
}

function normalizeDetails(
  type: EncounterRegistrationType,
  details: EncounterRegistrationDetails,
) {
  const normalized = { ...details };
  if (type === "emergency") {
    normalized.incident_datetime = joinLocalDateTime(
      stringValue(details.incident_date),
      stringValue(details.incident_time),
    );
  }
  if (type === "day_care") {
    normalized.scheduled_start = joinLocalDateTime(
      stringValue(details.scheduled_start_date),
      stringValue(details.scheduled_start_time),
    );
    normalized.expected_discharge = joinLocalDateTime(
      stringValue(details.expected_discharge_date),
      stringValue(details.expected_discharge_time),
    );
  }
  if (type === "newborn") {
    normalized.birth_datetime = joinLocalDateTime(
      stringValue(details.birth_date),
      stringValue(details.birth_time),
    );
  }
  if (type === "health_checkup") {
    normalized.visit_datetime = joinLocalDateTime(
      stringValue(details.visit_date),
      stringValue(details.visit_time),
    );
  }
  return normalized;
}
